import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Animated,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { Ionicons, MaterialIcons, Octicons } from "@expo/vector-icons";

import Colors from "../../../../Resources/Color";
import HoverableTouchableOpacity from "../../../Components/HoverableTouchableOpacity";
import ThreeDotLoader from "../../../Components/ThreeDotLoader";
import FloatingMenu from "../../../Components/FloatingMenu";
import Tooltip from "../../../Components/Tooltip";
import useChevronRotation from "../../../hooks/useChevronRotation";
import { useDevice } from "../../../_layout";
import { FoodBrowserResultRow } from "../foodRows/FoodBrowserResultRow";
import { resolveFoodPickerPanelKeyAction } from "./foodPickerKeyboard";

function getScrollableNodeFromRef(scrollContainerRef) {
  const refCurrent = scrollContainerRef?.current;
  if (!refCurrent) {
    return null;
  }

  const scrollableNode = refCurrent.getScrollableNode?.();
  if (scrollableNode) {
    return scrollableNode;
  }

  if (refCurrent.scrollTop !== undefined) {
    return refCurrent;
  }

  const firstChild = refCurrent.children?.[0];
  if (firstChild && firstChild.scrollTop !== undefined) {
    return firstChild;
  }

  return null;
}

const activeFoodPickerPanelIds = [];
let foodPickerPanelSequence = 0;

const EMPTY_STATE_SEARCH_TINT = "rgba(63, 123, 141, 0.34)";

function registerFoodPickerPanelId(panelId) {
  if (!panelId) {
    return;
  }

  const existingIndex = activeFoodPickerPanelIds.indexOf(panelId);
  if (existingIndex !== -1) {
    activeFoodPickerPanelIds.splice(existingIndex, 1);
  }

  activeFoodPickerPanelIds.push(panelId);
}

function unregisterFoodPickerPanelId(panelId) {
  if (!panelId) {
    return;
  }

  const existingIndex = activeFoodPickerPanelIds.indexOf(panelId);
  if (existingIndex === -1) {
    return;
  }

  activeFoodPickerPanelIds.splice(existingIndex, 1);
}

function isTopFoodPickerPanelId(panelId) {
  if (!panelId || activeFoodPickerPanelIds.length === 0) {
    return false;
  }

  return activeFoodPickerPanelIds[activeFoodPickerPanelIds.length - 1] === panelId;
}

function assignNodeToRef(ref, node) {
  if (!ref) {
    return;
  }

  if (typeof ref === "function") {
    ref(node);
    return;
  }

  ref.current = node;
}

export function FoodPickerPanel({
  panelRootRef: forwardedPanelRootRef = null,
  t,
  searchInputRef,
  inputPressingRef,
  focusSearchInput,
  searchText,
  setSearchText,
  searchPlaceholder = null,
  sortOptions,
  selectedSortOption,
  sortKey,
  setSortKey,
  databaseOptions,
  selectedDatabaseOption,
  databaseKey,
  setDatabaseKey,
  results,
  getRowKey,
  getRowTitle,
  getRowSubtitle,
  getRowQuantityValue = () => "",
  getRowUnitOptions = () => [],
  getRowSelectedUnitKey = () => "grams",
  getRowReferenceText = () => "",
  getRowTotals = null,
  getRowEntityKind = () => "food",
  getRowEntityLabel = () => null,
  getRowPreviewText = () => "",
  canSelectRow = () => true,
  onSelectRow,
  onRowQuantityChange = () => {},
  onRowUnitChange = () => {},
  title,
  subtitle = null,
  emptyText,
  onClose,
  onCreateFood = null,
  isInline = false,
  compactLayout = false,
  isLoading = false,
  loadingSessionId = null,
  showRowQuantityControl = false,
  showRowNutritionSummary = true,
  rowNutritionSummaryLabel = null,
  selectSearchTextOnFocus = true,
  topMarginOverride = null,
  bottomMarginOverride = null,
}) {
  const { isMobile, isIOS } = useDevice();
  const [isSortTriggerHovered, setIsSortTriggerHovered] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [isDatabaseTriggerHovered, setIsDatabaseTriggerHovered] =
    useState(false);
  const [isDatabaseMenuOpen, setIsDatabaseMenuOpen] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const hasActiveSearch = searchText.trim().length > 0;
  const visibleResults = isLoading ? [] : results;
  const [highlightedIndex, setHighlightedIndex] = useState(
    hasActiveSearch && visibleResults.length > 0 ? 0 : -1,
  );
  const browserResultsScrollViewRef = useRef(null);
  const resultRowRefs = useRef([]);
  const keyboardNavigationRef = useRef(false);
  const panelIdRef = useRef(null);
  const panelRootRef = useRef(null);
  const sortChevronRotation = useChevronRotation(
    isSortMenuOpen,
    "closedDownOpenUp",
  );
  const databaseChevronRotation = useChevronRotation(
    isDatabaseMenuOpen,
    "closedDownOpenUp",
  );

  const ensurePanelId = useCallback(() => {
    if (!panelIdRef.current) {
      foodPickerPanelSequence += 1;
      panelIdRef.current = `food-picker-panel-${foodPickerPanelSequence}`;
    }

    return panelIdRef.current;
  }, []);

  const markPanelActive = useCallback(() => {
    registerFoodPickerPanelId(ensurePanelId());
  }, [ensurePanelId]);
  const handlePanelRootRef = useCallback(
    (node) => {
      panelRootRef.current = node;
      assignNodeToRef(forwardedPanelRootRef, node);
    },
    [forwardedPanelRootRef],
  );

  useEffect(() => {
    const panelId = ensurePanelId();
    registerFoodPickerPanelId(panelId);

    return () => {
      unregisterFoodPickerPanelId(panelId);
    };
  }, [ensurePanelId]);

  useEffect(() => {
    if (visibleResults.length === 0) {
      setHighlightedIndex(-1);
      keyboardNavigationRef.current = false;
      return;
    }

    setHighlightedIndex((currentIndex) => {
      if (currentIndex < 0) {
        return -1;
      }

      return Math.min(currentIndex, visibleResults.length - 1);
    });
  }, [visibleResults.length]);

  useEffect(() => {
    keyboardNavigationRef.current = false;
    setHighlightedIndex(hasActiveSearch && visibleResults.length > 0 ? 0 : -1);
  }, [hasActiveSearch, searchText, visibleResults.length]);

  useEffect(() => {
    resultRowRefs.current = resultRowRefs.current.slice(0, visibleResults.length);
  }, [visibleResults.length]);

  useEffect(() => {
    if (
      highlightedIndex < 0 ||
      !keyboardNavigationRef.current ||
      visibleResults.length === 0
    ) {
      return;
    }

    const rowElement = resultRowRefs.current[highlightedIndex];
    const scrollContainer = getScrollableNodeFromRef(browserResultsScrollViewRef);

    if (!rowElement || !scrollContainer) {
      return;
    }

    const isFirstItem = highlightedIndex === 0;
    const isLastItem = highlightedIndex === visibleResults.length - 1;

    if (isFirstItem) {
      browserResultsScrollViewRef.current?.scrollTo?.({ y: 0, animated: true });
      scrollContainer.scrollTop = 0;
      return;
    }

    if (isLastItem) {
      browserResultsScrollViewRef.current?.scrollToEnd?.({ animated: true });
      const containerHeight = scrollContainer.clientHeight;
      const scrollHeight = scrollContainer.scrollHeight;
      scrollContainer.scrollTop = scrollHeight - containerHeight;
      return;
    }

    rowElement.scrollIntoView?.({
      block: "nearest",
      behavior: "smooth",
    });
  }, [highlightedIndex, visibleResults.length]);

  const handleSearchInputKeyDown = useCallback(
    (event) => {
      const key = event?.nativeEvent?.key ?? event?.key;
      if (!key || visibleResults.length === 0) {
        return;
      }

      if (key === "ArrowDown") {
        event.preventDefault?.();
        event.stopPropagation?.();
        keyboardNavigationRef.current = true;
        setHighlightedIndex((currentIndex) => {
          const baseIndex = currentIndex < 0 ? 0 : currentIndex;
          return (baseIndex + 1) % visibleResults.length;
        });
        return;
      }

      if (key === "ArrowUp") {
        event.preventDefault?.();
        event.stopPropagation?.();
        keyboardNavigationRef.current = true;
        setHighlightedIndex((currentIndex) => {
          if (currentIndex < 0) {
            return visibleResults.length - 1;
          }

          return (currentIndex - 1 + visibleResults.length) % visibleResults.length;
        });
      }
    },
    [visibleResults.length],
  );

  useEffect(() => {
    if (!isSearchFocused || typeof window === "undefined") {
      return;
    }

    const onWindowKeyDown = (event) => {
      const key = event?.key;

      if (key !== "ArrowDown" && key !== "ArrowUp") {
        return;
      }

      handleSearchInputKeyDown(event);
    };

    window.addEventListener("keydown", onWindowKeyDown, true);
    return () => window.removeEventListener("keydown", onWindowKeyDown, true);
  }, [handleSearchInputKeyDown, isSearchFocused]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof onClose !== "function") {
      return undefined;
    }

    const onWindowKeyDown = (event) => {
      const action = resolveFoodPickerPanelKeyAction({
        event,
        activeElement:
          typeof document !== "undefined" ? document.activeElement : null,
        panelElement: () => panelRootRef.current,
        isTopMost: () => isTopFoodPickerPanelId(panelIdRef.current),
      });

      if (action !== "close") {
        return;
      }

      event?.preventDefault?.();
      event?.stopPropagation?.();
      event?.stopImmediatePropagation?.();
      onClose();
    };

    window.addEventListener("keydown", onWindowKeyDown, true);
    return () => window.removeEventListener("keydown", onWindowKeyDown, true);
  }, [onClose]);

  const handleSearchSubmit = useCallback(() => {
    const selectedResult =
      highlightedIndex >= 0 ? visibleResults[highlightedIndex] : null;

    if (!selectedResult || !canSelectRow(selectedResult)) {
      return;
    }

    onSelectRow(selectedResult);
  }, [canSelectRow, highlightedIndex, onSelectRow, visibleResults]);

  const handleSearchTextChange = useCallback(
    (value) => {
      keyboardNavigationRef.current = false;
      setSearchText(value);
    },
    [setSearchText],
  );

  const handleSearchFieldPress = useCallback(() => {
    focusSearchInput();
  }, [focusSearchInput]);

  return (
    <View
      ref={handlePanelRootRef}
      style={[
        styles.browserCard,
        isInline ? styles.browserCardInline : null,
        topMarginOverride != null ? { marginTop: topMarginOverride } : null,
        bottomMarginOverride != null
          ? { marginBottom: bottomMarginOverride }
          : null,
      ]}
      onMouseDown={markPanelActive}
      onTouchStart={markPanelActive}
    >
      <View
        style={[
          styles.browserTopSection,
          isInline ? styles.browserTopSectionInline : null,
        ]}
      >
        <View style={styles.browserHeader}>
          <View style={styles.browserHeaderTextWrap}>
            <Text style={styles.browserTitle}>{title}</Text>
            {subtitle ? <Text style={styles.browserSubtitle}>{subtitle}</Text> : null}
          </View>
          <HoverableTouchableOpacity
            activeOpacity={0.75}
            onPress={onClose}
            style={styles.browserCloseButton}
            hoverStyle={styles.browserCloseButtonHover}
          >
            {(isHovered) => (
              <Ionicons
                name="close"
                size={17}
                color={isHovered ? Colors.darkgrayColor : Colors.grayColor}
              />
            )}
          </HoverableTouchableOpacity>
        </View>

        <View style={styles.browserSearchRow}>
          <View style={styles.browserSearchField}>
            <TouchableOpacity
              activeOpacity={1}
              onPress={handleSearchFieldPress}
              style={styles.browserSearchInputPressable}
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel={searchPlaceholder || t("searchFood-string")}
              aria-label={searchPlaceholder || t("searchFood-string")}
            >
              <View style={styles.browserSearchIcon}>
                <Octicons name="search" size={14} color={Colors.grayColor} />
              </View>
              <TextInput
                ref={searchInputRef}
                value={searchText}
                onChangeText={handleSearchTextChange}
                onSubmitEditing={handleSearchSubmit}
                onKeyPress={handleSearchInputKeyDown}
                onFocus={() => {
                  markPanelActive();
                  setIsSearchFocused(true);
                }}
                onPressIn={() => {
                  inputPressingRef.current = true;
                }}
                onPressOut={() => {
                  inputPressingRef.current = false;
                }}
                onBlur={() => {
                  inputPressingRef.current = false;
                  setIsSearchFocused(false);
                }}
                placeholder={searchPlaceholder || t("searchFood-string")}
                placeholderTextColor={Colors.grayColor}
                style={[
                  styles.browserSearchInput,
                  isMobile && isIOS ? styles.browserSearchInputIOS : null,
                ]}
                blurOnSubmit={false}
                returnKeyType="search"
                selectTextOnFocus={selectSearchTextOnFocus}
                accessibilityLabel={searchPlaceholder || t("searchFood-string")}
                aria-label={searchPlaceholder || t("searchFood-string")}
              />
            </TouchableOpacity>

            {typeof onCreateFood === "function" ? (
              <Tooltip content="addCustomFood-string" placement="top">
                <View>
                  <HoverableTouchableOpacity
                    activeOpacity={0.82}
                    onPress={() => {
                      markPanelActive();
                      onCreateFood();
                    }}
                    style={styles.browserCreateFoodTrigger}
                    hoverStyle={styles.browserCreateFoodTriggerHover}
                    accessible={true}
                    accessibilityRole="button"
                    accessibilityLabel={t("addCustomFood-string")}
                    aria-label={t("addCustomFood-string")}
                  >
                    {(isHovered) => (
                      <Ionicons
                        name="add"
                        size={16}
                        color={
                          isHovered ? Colors.darkgrayColor : Colors.bubbleColor
                        }
                      />
                    )}
                  </HoverableTouchableOpacity>
                </View>
              </Tooltip>
            ) : null}

            <View style={styles.browserSearchFieldDivider} />

            <FloatingMenu
              placement="bottom-end"
              width={220}
              items={sortOptions}
              open={isSortMenuOpen}
              onOpenChange={setIsSortMenuOpen}
              trigger={
                <View
                  style={styles.browserSortTrigger}
                  onMouseEnter={
                    !isMobile ? () => setIsSortTriggerHovered(true) : undefined
                  }
                  onMouseLeave={
                    !isMobile ? () => setIsSortTriggerHovered(false) : undefined
                  }
                >
                  <MaterialIcons
                    name="sort"
                    size={14}
                    color={Colors.darkgrayColor}
                  />
                  {selectedSortOption?.field == null ? (
                    <Text
                      style={[
                        styles.browserSortTriggerLabel,
                        isSortTriggerHovered
                          ? styles.browserSortTriggerLabelHovered
                          : null,
                      ]}
                    >
                      {t("sortBy-string")}
                    </Text>
                  ) : (
                    <>
                      <Text
                        style={[
                          styles.browserSortTriggerLabel,
                          isSortTriggerHovered
                            ? styles.browserSortTriggerLabelHovered
                            : null,
                        ]}
                      >
                        {t("sortBy-string")}
                      </Text>
                      <Text style={styles.browserSortTriggerValue}>
                        {selectedSortOption?.label}
                      </Text>
                    </>
                  )}
                  <Animated.View
                    style={{ transform: [{ rotate: sortChevronRotation }] }}
                  >
                    <Ionicons
                      name="chevron-down"
                      size={14}
                      color={
                        isSortTriggerHovered ? Colors.darkgrayColor : Colors.grayColor
                      }
                    />
                  </Animated.View>
                </View>
              }
              renderItem={({ item, close }) => (
                <HoverableTouchableOpacity
                  style={styles.menuItem}
                  hoverStyle={styles.menuItemHover}
                  onPress={() => {
                    setSortKey(item.key);
                    close();
                  }}
                >
                  <Text
                    style={[
                      styles.menuItemText,
                      item.key === sortKey ? styles.menuItemTextActive : null,
                    ]}
                  >
                    {item.label}
                  </Text>
                  {item.key === sortKey ? (
                    <Ionicons
                      name="checkmark"
                      size={15}
                      color={Colors.bubbleColor}
                    />
                  ) : null}
                </HoverableTouchableOpacity>
              )}
            />
          </View>

          <FloatingMenu
            placement="bottom-end"
            width={220}
            items={databaseOptions}
            open={isDatabaseMenuOpen}
            onOpenChange={setIsDatabaseMenuOpen}
            trigger={
              <View
                style={[
                  styles.browserMenuTrigger,
                  isDatabaseTriggerHovered
                    ? styles.browserMenuTriggerHover
                    : null,
                ]}
                onMouseEnter={
                  !isMobile ? () => setIsDatabaseTriggerHovered(true) : undefined
                }
                onMouseLeave={
                  !isMobile
                    ? () => setIsDatabaseTriggerHovered(false)
                    : undefined
                }
              >
                <Ionicons name="layers-outline" size={14} color={Colors.grayColor} />
                <Text
                  style={[
                    styles.browserMenuTriggerText,
                    isDatabaseTriggerHovered
                      ? styles.browserMenuTriggerTextHovered
                      : null,
                  ]}
                >
                  {selectedDatabaseOption?.label}
                </Text>
                <Animated.View
                  style={{ transform: [{ rotate: databaseChevronRotation }] }}
                >
                  <Ionicons
                    name="chevron-down"
                    size={14}
                    color={
                      isDatabaseTriggerHovered
                        ? Colors.darkgrayColor
                        : Colors.grayColor
                    }
                  />
                </Animated.View>
              </View>
            }
            renderItem={({ item, close }) => (
              <HoverableTouchableOpacity
                style={styles.menuItem}
                hoverStyle={styles.menuItemHover}
                onPress={() => {
                  setDatabaseKey(item.key);
                  close();
                }}
              >
                <Text
                  style={[
                    styles.menuItemText,
                    item.key === databaseKey ? styles.menuItemTextActive : null,
                  ]}
                >
                  {item.label}
                </Text>
                {item.key === databaseKey ? (
                  <Ionicons
                    name="checkmark"
                    size={15}
                    color={Colors.bubbleColor}
                  />
                ) : null}
              </HoverableTouchableOpacity>
            )}
          />

        </View>
      </View>

      <View
        style={[
          styles.browserResultsViewport,
          isInline ? styles.browserResultsViewportInline : null,
        ]}
      >
        {isLoading ? (
          <View style={styles.inlineEmptyState}>
            <ThreeDotLoader
              key={`food-picker-loading-session-${loadingSessionId ?? 0}`}
              color={Colors.bubbleColor}
            />
          </View>
        ) : visibleResults.length === 0 ? (
          <View style={styles.emptyStateContainer}>
            <View accessibilityRole="image" accessibilityLabel={emptyText}>
              <Ionicons name="search-outline" size={26} color={EMPTY_STATE_SEARCH_TINT} />
            </View>
            <Text style={styles.emptyStateText}>{emptyText}</Text>
          </View>
        ) : (
          <ScrollView
            ref={browserResultsScrollViewRef}
            showsVerticalScrollIndicator={true}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={[
              styles.browserResultsContent,
              isInline ? styles.browserResultsContentInline : null,
            ]}
          >
            {visibleResults.map((item, index) => {
              const isHighlighted = highlightedIndex === index;

              return (
                <FoodBrowserResultRow
                  key={getRowKey(item, index)}
                  rowRef={(node) => {
                    resultRowRefs.current[index] = node;
                  }}
                  isInline={isInline}
                  isHighlighted={isHighlighted}
                  compactLayout={compactLayout}
                  canSelect={canSelectRow(item)}
                  onPress={() => onSelectRow(item)}
                  onHover={() => {
                    keyboardNavigationRef.current = false;
                    setHighlightedIndex(index);
                  }}
                  quantityValue={getRowQuantityValue(item)}
                  onChangeQuantity={(value) => onRowQuantityChange(item, value)}
                  onSubmitQuantity={() => {
                    if (canSelectRow(item)) {
                      onSelectRow(item);
                    }
                  }}
                  unitOptions={getRowUnitOptions(item)}
                  selectedUnitKey={getRowSelectedUnitKey(item)}
                  onSelectUnit={(nextUnitKey) =>
                    onRowUnitChange(item, nextUnitKey)
                  }
                  referenceText={getRowReferenceText(item)}
                  title={getRowTitle(item)}
                  subtitle={getRowSubtitle(item)}
                  entityKind={getRowEntityKind(item)}
                  entityLabel={getRowEntityLabel(item)}
                  previewText={getRowPreviewText(item)}
                  totals={showRowNutritionSummary ? getRowTotals?.(item) : null}
                  showQuantityControl={showRowQuantityControl}
                  showNutritionSummary={showRowNutritionSummary}
                  nutritionSummaryLabel={rowNutritionSummaryLabel}
                />
              );
            })}
          </ScrollView>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  browserCard: {
    marginTop: 18,
    padding: 0,
    position: "relative",
    zIndex: 1,
    borderRadius: 14,
    overflow: "hidden",
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
  },
  browserCardInline: {
    marginTop: 12,
    padding: 0,
  },
  browserTopSection: {
    backgroundColor: Colors.lightGrayColor,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: Colors.borderColor,
  },
  browserTopSectionInline: {
    paddingHorizontal: 14,
    paddingTop: 14,
    paddingBottom: 4,
  },
  browserHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 12,
  },
  browserHeaderTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  browserTitle: {
    fontSize: 14,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  browserSubtitle: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: "400",
    color: Colors.grayColor,
  },
  browserCloseButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  browserCloseButtonHover: {
    backgroundColor: Colors.hoveredGray,
  },
  browserSearchRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  browserSearchField: {
    flex: 1,
    minWidth: 220,
    minHeight: 42,
    borderRadius: 12,
    backgroundColor: Colors.whiteColor,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    flexDirection: "row",
    alignItems: "center",
    overflow: "hidden",
  },
  browserSearchInputPressable: {
    flex: 1,
    minWidth: 0,
    minHeight: 42,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    cursor: "text",
  },
  browserSearchIcon: {
    justifyContent: "center",
    alignItems: "center",
  },
  browserSearchInput: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    fontWeight: "400",
    color: Colors.blackColor,
    outlineStyle: "none",
    marginLeft: 10,
    paddingVertical: 10,
  },
  browserSearchInputIOS: {
    fontSize: 16,
  },
  browserSearchFieldDivider: {
    width: 0.5,
    height: 18,
    alignSelf: "center",
    backgroundColor: Colors.borderColor,
  },
  browserCreateFoodTrigger: {
    width: 34,
    height: 34,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
    marginHorizontal: 4,
    flexShrink: 0,
  },
  browserCreateFoodTriggerHover: {
    backgroundColor: Colors.hoveredGray,
  },
  browserSortTrigger: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 8,
    flexShrink: 0,
  },
  browserSortTriggerLabel: {
    fontSize: 12,
    fontWeight: "400",
    color: Colors.grayColor,
  },
  browserSortTriggerLabelHovered: {
    color: Colors.darkgrayColor,
  },
  browserSortTriggerValue: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  browserMenuTrigger: {
    minHeight: 42,
    paddingHorizontal: 12,
    borderRadius: 12,
    backgroundColor: Colors.whiteColor,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  browserMenuTriggerHover: {
    backgroundColor: "rgba(248, 250, 251, 0.15)",
  },
  browserMenuTriggerText: {
    fontSize: 13,
    fontWeight: "400",
    color: Colors.darkgrayColor,
  },
  browserMenuTriggerTextHovered: {
    color: Colors.blackColor,
  },
  menuItem: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderRadius: 8,
  },
  menuItemHover: {
    backgroundColor: Colors.hoveredGray,
  },
  menuItemText: {
    fontSize: 12,
    fontWeight: "400",
    color: Colors.darkgrayColor,
    flexShrink: 1,
  },
  menuItemTextActive: {
    fontWeight: "500",
    color: Colors.bubbleColor,
  },
  browserResultsViewport: {
    height: 390,
  },
  browserResultsViewportInline: {
    height: 320,
  },
  browserResultsContent: {
    paddingHorizontal: 16,
    paddingTop: 0,
    paddingBottom: 0,
  },
  browserResultsContentInline: {
    paddingHorizontal: 14,
    paddingTop: 0,
    paddingBottom: 0,
  },
  inlineEmptyState: {
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 12,
  },
  emptyStateContainer: {
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 32,
    gap: 16,
  },
  emptyStateText: {
    fontSize: 13,
    fontWeight: "400",
    color: Colors.grayColor,
    textAlign: "center",
    lineHeight: 18,
    maxWidth: 240,
  },
});
